import React, { useState, useEffect } from 'react';
import {
  Box,
  Collapse,
} from '@mui/material';
import { TLookupTextField } from '../../../../components/common';
import { type DERCFiltered, type DERCResponse } from '../types/derc';
import { DERCService } from '../services/dercService';
import DercSelectionModal from './DercSelectionModal';
import DercInfoPanel from './DercInfoPanel';

interface DercLookupProps {
  label?: string;
  value?: string;
  onChange?: (idDocumento: number, displayText: string, dercData?: DERCResponse) => void;
  error?: string;
  required?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  modalTitle?: string;
  showInfoPanel?: boolean;
  disabled?: boolean;
}

const DercLookup: React.FC<DercLookupProps> = ({
  label = 'N° DERC',
  value = '',
  onChange,
  error,
  required = false,
  fullWidth = true,
  placeholder = 'Seleccione un DERC',
  modalTitle = 'Seleccionar DERC',
  showInfoPanel = true,
  disabled = false
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIdDocumento, setSelectedIdDocumento] = useState<number | null>(null);
  const [dercDetails, setDercDetails] = useState<DERCResponse | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleClear = () => {
    if (onChange) {
      onChange(0, '', undefined); // Reset to empty values
    }
    setSelectedIdDocumento(null);
    setDercDetails(null);
    setDetailsError(null);
  };

  const fetchDercDetails = async (idDocumento: number, notifyOnChange: boolean = false) => {
    setLoadingDetails(true);
    setDetailsError(null);

    try {
      const details = await DERCService.getDERCById(idDocumento);
      setDercDetails(details);

      // Pasar los detalles del DERC al callback onChange para que el formulario padre los pueda usar
      if (notifyOnChange && onChange && details) {
        onChange(idDocumento, `DERC ${details.numeroDocumento}`, details);
      }
    } catch (error) {
      console.error('Error fetching DERC details:', error);
      setDetailsError('Error al cargar los detalles del DERC');
      setDercDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectDerc = async (derc: DERCFiltered) => {
    setSelectedIdDocumento(derc.idDocumento);
    setModalOpen(false);

    // Always fetch full DERC details to get isEntero and other properties
    // This ensures the parent form has all the data it needs
    setLoadingDetails(true);
    try {
      const details = await DERCService.getDERCById(derc.idDocumento);
      setDercDetails(details);

      // Pass full details to parent callback
      if (onChange && details) {
        onChange(derc.idDocumento, `DERC ${details.numeroDocumento}`, details);
      }
    } catch (error) {
      console.error('Error fetching DERC details:', error);
      // Fallback: notify parent with basic info
      if (onChange) {
        onChange(derc.idDocumento, `DERC ${derc.idDocumento}`);
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  // Effect to fetch details when DERC ID changes (for showInfoPanel display)
  useEffect(() => {
    if (selectedIdDocumento && showInfoPanel && !dercDetails) {
      fetchDercDetails(selectedIdDocumento);
    } else if (!selectedIdDocumento) {
      setDercDetails(null);
      setDetailsError(null);
    }
  }, [selectedIdDocumento, showInfoPanel]);

  // Effect to sync selectedIdDocumento when value changes externally
  useEffect(() => {
    if (!value) {
      setSelectedIdDocumento(null);
      setDercDetails(null);
      setDetailsError(null);
    }
  }, [value]);

  return (
    <Box sx={{ position: 'relative' }}>
      <TLookupTextField
        label={label}
        value={value}
        error={error}
        required={required}
        fullWidth={fullWidth}
        placeholder={placeholder}
        onSearchClick={disabled ? undefined : handleOpenModal}
        onClear={disabled ? undefined : handleClear}
        searchTooltip="Buscar DERC"
        clearTooltip="Limpiar DERC"
        disabled={disabled}
      />

      {/* DERC Information Panel */}
      {showInfoPanel && (
        <Collapse in={!!selectedIdDocumento && !!value}>
          <DercInfoPanel
            dercDetails={dercDetails}
            loading={loadingDetails}
            error={detailsError}
          />
        </Collapse>
      )}

      <DercSelectionModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSelect={handleSelectDerc}
        title={modalTitle}
      />
    </Box>
  );
};

export default DercLookup;